import { useQuery } from '@tanstack/react-query';
import { logger } from '@/utils/logger';
import { resolveToPrimarySync } from '@/hooks/usePrimaryLocale';

// Structure from locales.json (pre-computed at export time)
export interface BundledLocale {
  code: string;       // Primary language code (e.g., "ar", "en", "pt")
  english: string;    // English name (e.g., "Arabic")
  native: string;     // Native name (e.g., "العربية")
  shorts: Record<string, string>;  // 36 translations { ar: "العربية", en: "Arabic", pt: "árabe", ... }
}

export interface LocalizedLanguage {
  code: string;        // Base language code (unique key for React)
  locale: string;      // Locale code for i18next (same as code for base languages)
  language: string;    // Base language code (e.g., 'en', 'es', 'pt')
  country: string;     // Country code (empty for base languages, kept for compatibility)
  name: string;        // Primary display: native name (for compatibility)
  native: string;      // Native name (e.g., "العربية")
  english: string;     // English name (e.g., "Arabic")
  search: string;      // Search terms combined
  short: string;       // Short name (empty for base languages)
  shorts: Record<string, string>;  // Empty for base languages
  names: Record<string, string>;   // All 36 translations
}

// Module-level cache for the bundled data
let cachedLocales: BundledLocale[] | null = null;
let cachePromise: Promise<BundledLocale[]> | null = null;

/**
 * Fetches bundled locale data from static JSON with proper caching.
 * Ensures only one fetch happens even with concurrent calls.
 */
async function fetchBundledLocales(): Promise<BundledLocale[]> {
  if (cachedLocales) {
    return cachedLocales;
  }

  if (cachePromise) {
    return cachePromise;
  }

  cachePromise = (async () => {
    try {
      const response = await fetch('/data/locales.json');
      if (!response.ok) {
        logger.error('Failed to load locales.json', { status: response.status });
        return [];
      }
      cachedLocales = await response.json();
      return cachedLocales!;
    } catch (error) {
      logger.error('Error loading locales.json', { error });
      return [];
    } finally {
      cachePromise = null;
    }
  })();

  return cachePromise;
}

/**
 * Maps bundled locales to LocalizedLanguage format.
 * Maintains backward compatibility with components expecting locale, language, country, name properties.
 */
function mapToLocalizedLanguages(locales: BundledLocale[]): LocalizedLanguage[] {
  return locales.map((loc): LocalizedLanguage => {
    const searchTerms = [loc.native, loc.english, loc.code].filter(Boolean).join(' ');

    return {
      code: loc.code,
      locale: loc.code,      // Same as code for base languages
      language: loc.code,    // Base language code
      country: '',           // Empty for base languages
      name: loc.native,      // Primary display is native name
      native: loc.native,
      english: loc.english,
      search: searchTerms,
      short: '',             // Empty for base languages
      shorts: loc.shorts,    // 36 translations
      names: loc.shorts,     // Alias for backward compatibility
    };
  });
}

/**
 * Hook to fetch language data with pre-computed translations.
 * 
 * @description
 * This is the UNIFIED hook for all language data in the app.
 * Shows only base languages (LinkedIn-style) without regional variants.
 * 
 * Uses React Query for caching and loads from static JSON at `/data/languages.json`.
 * 
 * **Data Source:**
 * - Static JSON file at `/data/languages.json` (single source of truth)
 * - Pre-computed at export time using Intl.DisplayNames
 * 
 * **When to use:**
 * - Language selector dropdowns (footer)
 * - Bio editor language tabs
 * - Any component showing language names to end users
 * - Locale badges and labels
 * 
 * @returns {{ languages: LocalizedLanguage[], isLoading: boolean, error: Error | null, getLanguageByCode, getLocalizedName, formatShortDisplay, formatCompactDisplay }}
 */
export function useLocalizedLanguages() {
  const { data: languages = [], isLoading, error } = useQuery({
    queryKey: ['localized-languages-from-locales'],
    queryFn: async () => {
      const bundled = await fetchBundledLocales();
      return mapToLocalizedLanguages(bundled);
    },
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
  });

  // Helper to get a language by its code with fallback chain
  const getLanguageByCode = (code: string): LocalizedLanguage | undefined => {
    // 1. Try exact match first
    const exactMatch = languages.find(l => l.code === code);
    if (exactMatch) return exactMatch;
    
    // 2. Handle Chinese variants explicitly (never use naive split for zh-*)
    // locale-logic: chinese-variants - zh-TW/zh-HK/zh-CN are distinct display languages
    const CHINESE_MAPPINGS: Record<string, string> = {
      'zh-HK': 'zh-TW',      // Hong Kong → Traditional Chinese
      'zh-Hant': 'zh-TW',    // Traditional script → Taiwan
      'zh-Hans': 'zh-CN',    // Simplified script → China
      'zh-SG': 'zh-CN',      // Singapore → Simplified Chinese
      'zh-MO': 'zh-TW',      // Macau → Traditional Chinese
    };
    
    if (CHINESE_MAPPINGS[code]) {
      const mapped = CHINESE_MAPPINGS[code];
      return languages.find(l => l.code === mapped);
    }
    
    // 3. For non-Chinese: try matching by base language code
    // locale-logic: display-only - Language dropdown shows base languages, safe to use split
    if (!code.startsWith('zh')) {
      const baseCode = code.split('-')[0];
      const baseMatch = languages.find(l => l.code === baseCode);
      if (baseMatch) return baseMatch;
    }
    
    // 4. Final fallback to English
    return languages.find(l => l.code === 'en');
  };

  /**
   * Get the localized name of a language in a specific locale.
   * @param langCode - The language code to get the name for (e.g., "ar")
   * @param userLocale - The locale to display the name in (e.g., "es" → "árabe")
   * @returns The localized name, falling back to English then native name
   */
  const getLocalizedName = (langCode: string, userLocale: string): string => {
    const lang = getLanguageByCode(langCode);
    if (!lang) return langCode;
    
    // Try exact locale match
    if (lang.names[userLocale]) return lang.names[userLocale];
    
    // Try primary locale (e.g., "es-MX" → "es", "zh-HK" → "zh-TW")
    const primaryLocale = resolveToPrimarySync([], userLocale);
    if (lang.names[primaryLocale]) return lang.names[primaryLocale];
    
    // Fallback to English
    if (lang.names['en']) return lang.names['en'];
    
    // Final fallback to native
    return lang.native;
  };

  // Helper to format display text: just native name for base languages
  const formatShortDisplay = (lang: LocalizedLanguage): string => {
    return lang.native;
  };

  // Helper to format compact display text: just native name
  const formatCompactDisplay = (lang: LocalizedLanguage): string => {
    return lang.native;
  };

  return { 
    languages, 
    isLoading, 
    error,
    getLanguageByCode,
    getLocalizedName,
    formatShortDisplay,
    formatCompactDisplay,
  };
}
